import { ArrowPathIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { createPortal } from 'react-dom';

const updatingBadgeClassName =
  'inline-flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-indigo-800 shadow-md shadow-indigo-900/10 backdrop-blur-sm';

/** Centered spinner for first load or when no cached dashboard data exists yet. */
export const BandwidthLoadingPanel: React.FC<{ message?: string }> = ({
  message = 'Loading bandwidth dashboard…',
}) => (
  <div
    className="flex min-h-[420px] flex-col items-center justify-center rounded-2xl border border-indigo-100/80 bg-white/90 p-12 text-sm text-slate-500 shadow-sm shadow-indigo-900/5 ring-1 ring-slate-950/[0.03]"
    role="status"
    aria-live="polite"
    aria-busy="true"
  >
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
    <p className="mt-3 text-sm text-slate-600">{message}</p>
  </div>
);

/** Inline Updating badge for panel title rows or the filter bar. */
export const BandwidthPanelUpdatingBadge: React.FC = () => (
  <span
    className={`${updatingBadgeClassName} px-2.5 py-1 text-[11px] shadow-sm`}
    aria-live="polite"
    aria-busy="true"
  >
    <ArrowPathIcon className="h-3 w-3 animate-spin text-indigo-600" aria-hidden />
    Updating
  </span>
);

/** Page-level Updating badge — portaled + fixed top-right; stays visible while scrolling. */
export const BandwidthFetchingIndicator: React.FC = () => {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`pointer-events-none fixed right-4 top-28 z-[9999] ${updatingBadgeClassName} sm:right-6 lg:right-8`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-indigo-600" aria-hidden />
      Updating
    </div>,
    document.body
  );
};
